import { UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';

import APIError from 'types/api_error';
import { PipelineTemplate } from 'types/pipeline_template';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';
import { sanitizeQueryKey } from 'services/query_key';

type UsePipelineTemplateOptions = UseQueryOptions<
  PipelineTemplate,
  APIError
> & {
  templateId: string | undefined;
};

export function usePipelineTemplate({
  templateId,
  queryKey,
  ...options
}: UsePipelineTemplateOptions) {
  const { applicationID, client } = useAPI();

  return useAuthenticatedQuery(
    queryKey ?? getPipelineTemplateQueryKey(applicationID, templateId),
    () => readPipelineTemplate(client, applicationID, templateId),
    {
      ...options,
      enabled: Boolean(templateId) && (options.enabled ?? true),
    }
  );
}

export function useReadPipelineTemplate() {
  const { applicationID, client } = useAPI();
  const queryClient = useQueryClient();

  return (templateId: PipelineTemplate['id']) =>
    queryClient.fetchQuery<PipelineTemplate, APIError>({
      queryKey: sanitizeQueryKey(
        getPipelineTemplateQueryKey(applicationID, templateId)
      ),
      queryFn: () => readPipelineTemplate(client, applicationID, templateId),
    });
}

function getPipelineTemplateQueryKey(
  applicationID: string | undefined,
  templateId: string | undefined
) {
  return ['pipeline-template', applicationID, templateId];
}

async function readPipelineTemplate(
  client: AxiosInstance,
  applicationID: string | undefined,
  templateId: string | undefined
) {
  if (!applicationID || !templateId) {
    throw {
      code: 'missing-route-parameters',
      status: 400,
      message: 'Missing pipeline template route parameters',
    } satisfies APIError;
  }

  const { data } = await client.get<PipelineTemplate>(
    `/internal/apps/${applicationID}/pipeline_templates/${templateId}`
  );

  return data;
}
